import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckSquare, Lightbulb, Plus, Sparkles } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";

import { cn } from "../../../lib/utils";
import { queryClient } from "../../../lib/queryClient";
import { dueLabelOf, dueDateText } from "../../../lib/dueDates";
import useRetryFetch from "../../../hooks/useRetryFetch";
import { useSpaces } from "../../../context/SpaceProvider";
import type { IdentitySpace } from "../../../api/identitySpaces";
import { WORKSPACE_STATS_KEY } from "../../../hooks/usePortalData";
import {
  createTakeaway,
  listTakeaways,
  toggleStatus,
  type UserTakeaway,
} from "../../../api/userTakeaways";
import { listCategories, type UserCategory } from "../../../api/userCategories";
import { getMyReminders } from "../../../api/checkpointReminders";
import { listObjects, type MmObject } from "../../../api/mentalModels";
import ActionItemDialog from "./actionItems/ActionItemDialog";
import InsightDialog from "./insights/InsightDialog";
import { useProjectIndex } from "../../../hooks/useProjectIndex";
import { ProjectChip } from "./ProjectChip";

/**
 * Design 3a — the workspace as ONE board. No hub page, no tab-hopping: action
 * items, insights, reminders and mental models are all visible at once, each
 * panel a doorway into its full section.
 */

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

export const invalidateWorkspace = () => {
  queryClient.invalidateQueries({ queryKey: ["workspace"] });
  queryClient.invalidateQueries({ queryKey: WORKSPACE_STATS_KEY });
};

// ── Due-date reading shared by board + focus ─────────────────────────────────

export const DueChip = ({ item }: { item: UserTakeaway }) => {
  const { t, i18n } = useTranslation();
  if (!item.dueDate) return null;
  const label = dueLabelOf(item.dueDate, Date.now());
  if (label.kind === "today")
    return (
      <span className="text-[10.5px] font-medium text-red-600 whitespace-nowrap">
        {t("workspace_due_today")}
      </span>
    );
  if (label.kind === "carried_weekday" || label.kind === "carried_since")
    return (
      <span
        title={
          label.kind === "carried_since"
            ? t("workspace_carried_since", { date: dueDateText(label, i18n.language) })
            : undefined
        }
        className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 whitespace-nowrap"
      >
        {t("workspace_carried")}
      </span>
    );
  return (
    <span className="text-[10.5px] text-muted-foreground/70 whitespace-nowrap">
      {dueDateText(label, i18n.language, "short")}
    </span>
  );
};

export const relTime = (ts: number, lang: string) => {
  const days = Math.round((Date.now() - ts) / (24 * 60 * 60 * 1000));
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: "always", style: "narrow" });
  if (days < 1) {
    const hours = Math.max(1, Math.round((Date.now() - ts) / (60 * 60 * 1000)));
    return rtf.format(-hours, "hour");
  }
  return rtf.format(-days, "day");
};

// ── Identity-space indicators shared by board + focus ────────────────────────

const SPACE_FALLBACK_COLOR = "#6366f1";

export const spacesOf = (item: UserTakeaway, spaces: IdentitySpace[] | undefined) =>
  (spaces ?? []).filter((s) => item.spaceIds?.includes(s._id));

/** Dot-only, for dense rows — names surface in the tooltip. */
export const SpaceDots = ({
  item,
  spaces,
}: {
  item: UserTakeaway;
  spaces: IdentitySpace[] | undefined;
}) => {
  const assigned = spacesOf(item, spaces);
  if (!assigned.length) return null;
  return (
    <span
      className="inline-flex items-center gap-1 flex-none"
      title={assigned.map((s) => s.name).join(" · ")}
    >
      {assigned.slice(0, 3).map((s) => (
        <span
          key={s._id}
          className="w-2 h-2 rounded-full"
          style={{ background: s.color || SPACE_FALLBACK_COLOR }}
        />
      ))}
    </span>
  );
};

/** Named mini-chips, for the focus list rows. */
export const SpaceMiniChips = ({
  item,
  spaces,
  max = 2,
}: {
  item: UserTakeaway;
  spaces: IdentitySpace[] | undefined;
  max?: number;
}) => {
  const assigned = spacesOf(item, spaces);
  if (!assigned.length) return null;
  return (
    <>
      {assigned.slice(0, max).map((s) => (
        <span
          key={s._id}
          className="text-[10.5px] px-1.5 py-px rounded-full inline-flex items-center gap-1 max-w-[140px]"
          style={{
            color: s.color || SPACE_FALLBACK_COLOR,
            background: `${s.color || SPACE_FALLBACK_COLOR}14`,
          }}
        >
          <span
            className="w-1.5 h-1.5 rounded-full flex-none"
            style={{ background: s.color || SPACE_FALLBACK_COLOR }}
          />
          <span className="truncate">{s.name}</span>
        </span>
      ))}
      {assigned.length > max && (
        <span
          className="text-[10.5px] text-muted-foreground/60"
          title={assigned.slice(max).map((s) => s.name).join(" · ")}
        >
          +{assigned.length - max}
        </span>
      )}
    </>
  );
};

/** Full badge, for the detail pane's badge row. */
export const SpaceBadge = ({ space }: { space: IdentitySpace }) => {
  const color = space.color || SPACE_FALLBACK_COLOR;
  return (
    <span
      className="text-[11px] px-2.5 py-[3px] rounded-full border inline-flex items-center gap-1.5"
      style={{ color, borderColor: `${color}55`, background: `${color}0d` }}
    >
      <span className="w-2 h-2 rounded-full flex-none" style={{ background: color }} />
      {space.name}
    </span>
  );
};

// ── Board ────────────────────────────────────────────────────────────────────

/** 6a's section pills — jump buttons over one scroll, tinted per zone. */
const JumpPill = ({
  onClick,
  tone,
  label,
  count,
}: {
  onClick: () => void;
  tone: "all" | "actions" | "insights" | "reminders" | "models";
  label: string;
  count?: number;
}) => (
  <button
    onClick={onClick}
    className={cn(
      "flex-none inline-flex items-center gap-1 text-[11.5px] px-3 py-1.5 rounded-full border whitespace-nowrap transition-colors",
      tone === "all" && "bg-foreground text-background border-foreground font-semibold",
      tone === "actions" && "border-primary/25 text-primary bg-primary/5",
      tone === "insights" && "border-amber-200 text-amber-700 bg-amber-50/60",
      tone === "reminders" && "border-purple-200 text-purple-700 bg-purple-50/60",
      tone === "models" && "border-border/70 text-muted-foreground bg-card"
    )}
  >
    {label}
    {typeof count === "number" && count > 0 && (
      <span className="tabular-nums font-medium">{count}</span>
    )}
  </button>
);

const WorkspaceBoard = ({
  onOpenSection,
  mobile = false,
}: {
  onOpenSection: (section: string, itemId?: string) => void;
  /** Design 6a — one scroll, section pills to jump; capture folds into one "+". */
  mobile?: boolean;
}) => {
  const { t, i18n } = useTranslation();
  const retryFetch = useRetryFetch();
  const { spaces } = useSpaces();
  const { projectById } = useProjectIndex();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState<"action" | "insight" | null>(null);

  const topRef = useRef<HTMLDivElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);
  const insightsRef = useRef<HTMLDivElement>(null);
  const remindersRef = useRef<HTMLDivElement>(null);
  const modelsRef = useRef<HTMLDivElement>(null);
  const jumpTo = (ref: React.RefObject<HTMLDivElement | null>) =>
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const { data: actionsData } = useQuery({
    queryKey: ["workspace", "board", "actions"],
    queryFn: () => listTakeaways(retryFetch, { type: "action", status: "pending", limit: 100 }),
    staleTime: 30_000,
  });
  const { data: insightsData } = useQuery({
    queryKey: ["workspace", "board", "insights"],
    queryFn: () => listTakeaways(retryFetch, { type: "insight", status: "pending", limit: 30 }),
    staleTime: 30_000,
  });
  const { data: remindersData } = useQuery({
    queryKey: ["workspace", "board", "reminders"],
    queryFn: () =>
      getMyReminders(retryFetch, { status: "pending", includeArchived: false }).catch(() => ({
        success: false,
        count: 0,
        reminders: [],
      })),
    staleTime: 60_000,
  });
  const { data: modelsData } = useQuery({
    queryKey: ["workspace", "board", "models"],
    queryFn: () => listObjects(retryFetch, { sort: "attention" }).catch(() => ({ status: "", objects: [] as MmObject[] })),
    staleTime: 60_000,
  });
  const { data: categoriesData } = useQuery({
    queryKey: ["workspace", "categories"],
    queryFn: () => listCategories(retryFetch).catch(() => ({ status: "LIST", list: [] as UserCategory[] })),
    staleTime: 5 * 60_000,
  });
  const categories = ((categoriesData as any)?.list ?? []) as UserCategory[];

  // Top-level actions only, dated ones first (soonest up), undated after
  const pendingActions = useMemo(
    () => (actionsData?.list ?? []).filter((a) => !a.parentId),
    [actionsData]
  );
  const { visibleActions, restingCount } = useMemo(() => {
    const dated = pendingActions
      .filter((a) => a.dueDate)
      .sort((a, b) => (a.dueDate ?? 0) - (b.dueDate ?? 0));
    const undated = pendingActions
      .filter((a) => !a.dueDate)
      .sort((a, b) => (b.createdOn ?? 0) - (a.createdOn ?? 0));
    const visible = [...dated, ...undated].slice(0, 5);
    const shownUndated = visible.filter((a) => !a.dueDate).length;
    return { visibleActions: visible, restingCount: undated.length - shownUndated };
  }, [pendingActions]);

  const insights = useMemo(() => insightsData?.list ?? [], [insightsData]);

  // A theme is only claimed when the data shows one: a category carried by 3+
  // insights inside the last week.
  const theme = useMemo(() => {
    const cutoff = Date.now() - WEEK_MS;
    const counts = new Map<string, number>();
    for (const ins of insights) {
      if ((ins.createdOn ?? 0) < cutoff || !ins.category?.name) continue;
      counts.set(ins.category.name, (counts.get(ins.category.name) ?? 0) + 1);
    }
    const top = Array.from(counts.entries()).sort((a, b) => b[1] - a[1])[0];
    return top && top[1] >= 3 ? { name: top[0], count: top[1] } : null;
  }, [insights]);

  const reminders = remindersData?.reminders ?? [];
  const models = (modelsData?.objects ?? []).filter((o) => o.status === "active");

  const toggle = async (item: UserTakeaway) => {
    if (busyId) return;
    setBusyId(item._id);
    try {
      await toggleStatus(retryFetch, { id: item._id });
      invalidateWorkspace();
    } catch (e: any) {
      toast.error(e.message ?? "Could not update");
    } finally {
      setBusyId(null);
    }
  };

  const saveNew = async (type: "action" | "insight", data: any) => {
    const response = await createTakeaway(retryFetch, {
      type,
      content: data.content,
      priority: data.priority || undefined,
      category: data.category || undefined,
      dueDate: data.dueDate ? data.dueDate.getTime() : undefined,
      spaceIds: data.spaceIds,
      isLongTermGoal: data.isLongTermGoal,
      source: { type: "user_created" },
    });
    if (response.status !== "CREATED") throw new Error(response.fault ?? "Could not save");
    toast.success(type === "action" ? t("action_item_created") : t("insight_created"));
    invalidateWorkspace();
  };

  const fmtReminderWhen = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const sameDay =
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate();
    const time = d.toLocaleTimeString(i18n.language, { hour: "numeric", minute: "2-digit" });
    return sameDay ? time : `${d.toLocaleDateString(i18n.language, { weekday: "short" })} ${time}`;
  };

  return (
    <div className="max-w-[1180px] mx-auto">
      {/* Header — one place to read the room; capture is two buttons on desktop,
          one "+" menu on mobile (6a) */}
      {mobile ? (
        <div ref={topRef} className="flex items-center justify-between gap-3 mb-3 scroll-mt-2">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold tracking-tight text-foreground">
              {t("my_workspace", "My Workspace")}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5 truncate">
              {t("workspace_board_subtitle")}
            </p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                aria-label={t("add")}
                className="w-9 h-9 flex-none rounded-[11px] bg-primary text-primary-foreground inline-flex items-center justify-center active:scale-95 transition-transform"
              >
                <Plus className="w-5 h-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setAddOpen("action")}>
                <CheckSquare className="w-4 h-4 me-2 text-primary" />
                {t("workspace_add_action", "Action item")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setAddOpen("insight")}>
                <Lightbulb className="w-4 h-4 me-2 text-amber-500" />
                {t("workspace_add_insight", "Insight")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : (
        <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3 mb-5">
          <div>
            <h2 className="text-[22px] font-semibold tracking-tight text-foreground">
              {t("my_workspace", "My Workspace")}
            </h2>
            <p className="text-[12.5px] text-muted-foreground mt-0.5">
              {t("workspace_board_subtitle")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAddOpen("action")}
              className="inline-flex items-center gap-1.5 rounded-[10px] border border-primary/20 bg-primary/5 px-3.5 py-[7px] text-[12.5px] font-medium text-primary hover:bg-primary/10 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              {t("workspace_add_action", "Action item")}
            </button>
            <button
              onClick={() => setAddOpen("insight")}
              className="inline-flex items-center gap-1.5 rounded-[10px] border border-amber-200 bg-amber-50 px-3.5 py-[7px] text-[12.5px] font-medium text-amber-700 hover:bg-amber-100/70 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              {t("workspace_add_insight", "Insight")}
            </button>
          </div>
        </div>
      )}

      {/* 6a — section pills jump within the single scroll */}
      {mobile && (
        <div className="flex gap-1.5 overflow-x-auto no-scrollbar -mx-3 px-3 mb-3">
          <JumpPill tone="all" label={t("workspace_pill_all")} onClick={() => jumpTo(topRef)} />
          <JumpPill
            tone="actions"
            label={t("actions")}
            count={actionsData?.total ?? pendingActions.length}
            onClick={() => jumpTo(actionsRef)}
          />
          <JumpPill
            tone="insights"
            label={t("insights")}
            count={insightsData?.total ?? insights.length}
            onClick={() => jumpTo(insightsRef)}
          />
          <JumpPill
            tone="reminders"
            label={t("workspace_pill_reminders")}
            count={reminders.length}
            onClick={() => jumpTo(remindersRef)}
          />
          <JumpPill
            tone="models"
            label={t("workspace_pill_models")}
            count={models.length}
            onClick={() => jumpTo(modelsRef)}
          />
        </div>
      )}

      <div
        className={cn(
          "grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-[1.35fr_1fr_300px] items-start",
          mobile ? "gap-3" : "gap-4"
        )}
      >
        {/* Action items */}
        <div ref={actionsRef} className="bg-card border border-border/60 rounded-2xl min-w-0 scroll-mt-2">
          <button
            onClick={() => onOpenSection("action_items")}
            className="w-full flex items-center justify-between px-[18px] pt-3.5 pb-2 text-start"
          >
            <span className="text-[13.5px] font-semibold text-foreground">
              {t("action_items")}
            </span>
            <span className="text-[11px] font-semibold text-primary bg-primary/10 rounded-full px-2.5 py-0.5">
              {t("workspace_pending_badge", { count: actionsData?.total ?? pendingActions.length })}
            </span>
          </button>
          <div className="px-2.5 pb-1.5">
            {visibleActions.length === 0 && (
              <p className="text-[12.5px] text-muted-foreground/70 px-2 py-3">
                {t("workspace_empty_actions")}
              </p>
            )}
            {visibleActions.map((item) => (
              <div
                key={item._id}
                className="flex items-center gap-2.5 px-2 py-[9px] rounded-[11px] hover:bg-primary/[0.03] transition-colors group"
              >
                <button
                  onClick={() => toggle(item)}
                  disabled={busyId === item._id}
                  aria-label={t("workspace_mark_done")}
                  className="w-[17px] h-[17px] rounded-full border-[1.5px] border-gray-300 hover:border-primary flex-none transition-colors disabled:opacity-50"
                />
                <button
                  onClick={() => onOpenSection("action_items", item._id)}
                  className="flex-1 min-w-0 text-start text-[13px] text-foreground/90 truncate"
                >
                  {item.content}
                </button>
                <SpaceDots item={item} spaces={spaces} />
                <ProjectChip item={item} projectById={projectById} spaces={spaces} />
                <DueChip item={item} />
              </div>
            ))}
            {restingCount > 0 && (
              <button
                onClick={() => onOpenSection("action_items")}
                className="flex items-center gap-2.5 px-2 py-[9px] w-full text-start"
              >
                <span className="w-[17px] flex-none" />
                <span className="text-[13px] text-muted-foreground/60">
                  {t("workspace_more_resting", { count: restingCount })}
                </span>
              </button>
            )}
          </div>
          {!mobile && (
            <div className="px-[18px] py-[9px] border-t border-border/40 text-[11.5px] text-muted-foreground/60">
              {t("workspace_grouped_hint")}
            </div>
          )}
        </div>

        {/* Insights */}
        <div ref={insightsRef} className="bg-card border border-border/60 rounded-2xl min-w-0 scroll-mt-2">
          <button
            onClick={() => onOpenSection("insights")}
            className="w-full flex items-center justify-between px-[18px] pt-3.5 pb-2 text-start"
          >
            <span className="text-[13.5px] font-semibold text-foreground">{t("insights")}</span>
            <span className="text-[11px] font-semibold text-amber-700 bg-amber-50 rounded-full px-2.5 py-0.5">
              {t("workspace_captured_badge", { count: insightsData?.total ?? insights.length })}
            </span>
          </button>
          <div className="px-3 pb-3 space-y-2">
            {insights.length === 0 && (
              <p className="text-[12.5px] text-muted-foreground/70 px-1 py-2">
                {t("workspace_empty_insights")}
              </p>
            )}
            {insights.slice(0, 3).map((ins) => (
              <button
                key={ins._id}
                onClick={() => onOpenSection("insights", ins._id)}
                className="w-full text-start border border-amber-200/80 bg-amber-50/40 rounded-xl px-[13px] py-[11px] hover:shadow-sm transition-shadow"
              >
                <p className="text-[12.5px] leading-[1.45] text-foreground/90 line-clamp-3">
                  {ins.content}
                </p>
                <p className="text-[10.5px] text-amber-700/90 mt-1.5 truncate flex items-center gap-1.5">
                  <span className="truncate">
                    {ins.source?.activityTitle ?? t("workspace_source_captured")}
                    {" · "}
                    {relTime(ins.createdOn, i18n.language)}
                  </span>
                  <SpaceDots item={ins} spaces={spaces} />
                  <ProjectChip item={ins} projectById={projectById} spaces={spaces} />
                </p>
              </button>
            ))}
            {theme && (
              <button
                onClick={() => onOpenSection("insights")}
                className="w-full text-start border border-border/60 bg-card rounded-xl px-[13px] py-[11px] hover:shadow-sm transition-shadow"
              >
                <p className="text-[12.5px] leading-[1.45] text-muted-foreground">
                  {t("workspace_clary_theme", { name: theme.name, count: theme.count })}
                </p>
                <p className="text-[10.5px] text-purple-600 mt-1.5 inline-flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  {t("workspace_suggested_by_clary")}
                </p>
              </button>
            )}
          </div>
        </div>

        {/* Right rail — reminders + mental models, quiet but present */}
        <div
          className={cn(
            "min-w-0 lg:col-span-2 xl:col-span-1 lg:grid lg:grid-cols-2 lg:gap-4 lg:space-y-0 xl:block",
            mobile ? "space-y-3" : "space-y-4 xl:space-y-4"
          )}
        >
          <div ref={remindersRef} className="bg-card border border-border/60 rounded-2xl px-4 py-3.5 scroll-mt-2">
            <button
              onClick={() => onOpenSection("reminders")}
              className="w-full flex items-center justify-between mb-2.5 text-start"
            >
              <span className="text-[13px] font-semibold text-foreground">
                {t("upcoming_reminders")}
              </span>
              <span className="text-[11px] font-semibold text-purple-600 bg-purple-50 rounded-full px-2.5 py-0.5">
                {reminders.length}
              </span>
            </button>
            {reminders.length === 0 ? (
              <p className="text-[12px] text-muted-foreground/70">
                {t("workspace_empty_reminders")}
              </p>
            ) : (
              <div className="space-y-2">
                {reminders.slice(0, 3).map((r, i) => (
                  <button
                    key={r._id}
                    onClick={() => onOpenSection("reminders", r._id)}
                    className="w-full flex items-baseline gap-2 text-start"
                  >
                    <span
                      className={cn(
                        "w-1.5 h-1.5 rounded-full flex-none translate-y-[-2px]",
                        i === 0 ? "bg-purple-500" : "bg-purple-300"
                      )}
                    />
                    <span className="flex-1 min-w-0 truncate text-[12.5px] text-foreground/80">
                      {r.activityTitle}
                    </span>
                    <span className="text-[10.5px] text-muted-foreground/70 whitespace-nowrap">
                      {fmtReminderWhen(r.scheduledDateTime)}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div ref={modelsRef} className="bg-card border border-border/60 rounded-2xl px-4 py-3.5 scroll-mt-2">
            <button
              onClick={() => onOpenSection("mental_models")}
              className="w-full flex items-center justify-between mb-2.5 text-start"
            >
              <span className="text-[13px] font-semibold text-foreground">
                {t("mm_studio_title", "Mental Models")}
              </span>
              <span className="text-[11px] font-semibold text-primary bg-primary/10 rounded-full px-2.5 py-0.5">
                {models.length}
              </span>
            </button>
            {models.length === 0 ? (
              <p className="text-[12px] text-muted-foreground/70">{t("workspace_empty_models")}</p>
            ) : (
              /* 6a shows the two models side by side */
              <div className={cn(mobile ? "grid grid-cols-2 gap-2" : "space-y-2")}>
                {models.slice(0, 2).map((m) => (
                  <button
                    key={m._id}
                    onClick={() => onOpenSection("mental_models", m._id)}
                    className="w-full text-start border border-border/60 rounded-[11px] px-3 py-2.5 hover:border-primary/30 transition-colors"
                  >
                    <p className="text-[12.5px] font-medium text-foreground/90 line-clamp-1">
                      {m.label || m.statement}
                    </p>
                    {typeof m.profile?.resolution === "number" && (
                      <div
                        className="h-[3px] rounded-full bg-gray-100 mt-2 overflow-hidden"
                        title={t("workspace_model_resolution")}
                      >
                        <div
                          className="h-full rounded-full bg-primary"
                          style={{ width: `${(m.profile.resolution / 5) * 100}%` }}
                        />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Capture dialogs — same forms as the full sections, opened right here */}
      <ActionItemDialog
        open={addOpen === "action"}
        onOpenChange={(o) => setAddOpen(o ? "action" : null)}
        onSave={(data) => saveNew("action", data)}
        categories={categories.filter((c) => c.type === "action" || c.type === "both")}
      />
      <InsightDialog
        open={addOpen === "insight"}
        onOpenChange={(o) => setAddOpen(o ? "insight" : null)}
        onSave={(data) => saveNew("insight", data)}
        categories={categories.filter((c) => c.type === "insight" || c.type === "both")}
      />
    </div>
  );
};

export default WorkspaceBoard;
